import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "../../../../hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { config } from "../../../../Environment";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import { Separator } from "../../../ui/separator";
import { Alert, AlertDescription } from "../../../ui/alert";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { useEffectOnce } from "../../../../hooks/useEffectOnce";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  UserPlus,
  Mail,
  CheckCircle2,
  Clock,
  Users,
  Send,
  AlertCircle,
} from "lucide-react";

interface MentorSharingDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  workflowId: string;
}

interface WorkflowUser {
  _id: string;
  fiClient: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

interface InvitedUser {
  email: string;
}

interface WorkflowMetadata {
  _id: string;
}

const MentorSharingDialogContent = ({
  workflowId,
}: {
  workflowId: string;
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [workflowMetadata, setWorkflowMetadata] = useState<WorkflowMetadata | null>(null);
  const [workflowUsers, setWorkflowUsers] = useState<WorkflowUser[] | null>(null);
  const [invitedUsers, setInvitedUsers] = useState<InvitedUser[] | null>(null);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const isLoading = useMemo<boolean>(() => {
    return Object.values(loading).some((status) => status);
  }, [loading]);
  const [shareWithUserVisible, setShareWithUserVisible] =
    useState<boolean>(false);
  const [shareWithUserEmail, setShareWithUserEmail] = useState<string>("");

  const retryFetch = useRetryFetch();

  async function loadWorkflowMetadata() {
    try {
      setLoading((prev) => ({ ...prev, loadWorkflowMetadata: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/prescriptions/${encodeURIComponent(workflowId)}/metadata?includeExpert=1`
      );
      const result = await response.json();
      setWorkflowMetadata(result);
    } catch (err) {
      console.log(`[MentorSharingDialog] loadWorkflowMetadata error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadWorkflowMetadata: false }));
    }
  }

  async function loadWorkflowUsers() {
    try {
      setLoading((prev) => ({ ...prev, loadWorkflowUsers: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/mentor/workflow/user/search?workflowId=${encodeURIComponent(workflowId)}&source=shared`
      );
      const result = await response.json();
      if (Array.isArray(result.list)) {
        setWorkflowUsers(result.list);
      }
    } catch (err) {
      console.log(`[MentorSharingDialog] loadWorkflowUsers error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadWorkflowUsers: false }));
    }
  }

  async function loadInvitedUsers() {
    try {
      setLoading((prev) => ({ ...prev, loadInvitedUsers: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/invitation/search?sent=1&action=run_workflow&state=pending&contextKey=context.workflowId&contextValue=${encodeURIComponent(workflowId)}`
      );
      const result = await response.json();
      if (Array.isArray(result.list)) {
        setInvitedUsers(result.list);
      }
    } catch (err) {
      console.log(`[MentorSharingDialog] loadInvitedUsers error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadInvitedUsers: false }));
    }
  }

  async function shareWithUser() {
    try {
      const email = shareWithUserEmail.trim();
      if (!email) {
        toast.error(t("email_required") || "Email is required");
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error(t("invalid_email") || "Invalid email address");
        return;
      }

      setLoading((prev) => ({ ...prev, shareWithUser: true }));
      const response = await retryFetch(`${config.API_BASE}/api/mentor/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email,
          workflowId,
        }),
      });
      const result = await response.json();

      if (result.type === "status") {
        if (result.status === "SHARED") {
          toast.success(
            t("activity_shared_successfully") || "Activity shared successfully!"
          );
          await loadWorkflowUsers();
        } else if (result.status === "INVITED") {
          toast.success(
            t("invitation_sent_successfully") ||
              "Invitation sent successfully!"
          );
          await loadInvitedUsers();
        } else {
          toast.error(t("error") || "An error occurred");
        }
        setShareWithUserVisible(false);
        setShareWithUserEmail("");
      }
    } catch (err) {
      console.log(`[MentorSharingDialog] shareWithUser error`, err);
      toast.error(t("failed_to_send_invitation") || "Failed to send invitation");
    } finally {
      setLoading((prev) => ({ ...prev, shareWithUser: false }));
    }
  }

  useEffectOnce(() => {
    (async function () {
      await loadWorkflowMetadata();
      await loadWorkflowUsers();
      await loadInvitedUsers();
    })();
  }, []);

  return (
    <div className="space-y-6">
      {/* Shared Users Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">
              {t("shared_with") || "Shared With"}
            </h3>
          </div>
          <Button
            size="sm"
            onClick={() => setShareWithUserVisible(true)}
            disabled={shareWithUserVisible || isLoading}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {t("share") || "Share"}
          </Button>
        </div>

        {/* Share Form */}
        {shareWithUserVisible && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="shareWithUserEmail">
                {t("email_address") || "Email Address"}
              </Label>
              <Input
                id="shareWithUserEmail"
                type="email"
                placeholder={t("enter_email") || "Enter email address"}
                value={shareWithUserEmail}
                onChange={(e) => setShareWithUserEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    shareWithUser();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={shareWithUser}
                disabled={
                  !shareWithUserEmail.trim() || loading.shareWithUser
                }
                className="flex-1"
              >
                {loading.shareWithUser ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {t("send_invitation") || "Send Invitation"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShareWithUserVisible(false);
                  setShareWithUserEmail("");
                }}
                disabled={loading.shareWithUser}
              >
                {t("cancel") || "Cancel"}
              </Button>
            </div>
          </div>
        )}

        {/* Shared Users List */}
        {loading.loadWorkflowUsers ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : workflowUsers && workflowUsers.length > 0 ? (
          <div className="space-y-2">
            {workflowUsers.map((item) => (
              <div
                key={item._id}
                className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="rounded-full bg-green-100 p-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {item.fiClient.displayName ??
                      `${item.fiClient.firstName} ${item.fiClient.lastName}`}
                  </p>
                  <p className="text-xs text-gray-600">{item.fiClient.email}</p>
                </div>
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  {t("active") || "Active"}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("workflow_not_shared") ||
                "This activity is not currently shared with anyone."}
            </AlertDescription>
          </Alert>
        )}
      </div>

      <Separator />

      {/* Pending Invitations Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">
            {t("pending_invitations") || "Pending Invitations"}
          </h3>
        </div>

        {loading.loadInvitedUsers ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : invitedUsers && invitedUsers.length > 0 ? (
          <div className="space-y-2">
            {invitedUsers.map((item) => (
              <div
                key={item.email}
                className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
              >
                <div className="rounded-full bg-yellow-100 p-2">
                  <Mail className="w-4 h-4 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {item.email}
                  </p>
                  <p className="text-xs text-gray-600">
                    {t("invitation_pending") || "Invitation pending"}
                  </p>
                </div>
                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                  {t("pending") || "Pending"}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("no_pending_invitations") || "No pending invitations."}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="w-5 h-5 animate-spin text-primary mr-2" />
          <span className="text-sm text-muted-foreground">
            {t("loading") || "Loading..."}
          </span>
        </div>
      )}
    </div>
  );
};

function MentorSharingDialog({
  open,
  onOpenChange,
  workflowId,
}: MentorSharingDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("manage_sharing") || "Manage Sharing"}</DialogTitle>
          <DialogDescription>
            {t("manage_sharing_description") ||
              "Share this activity with clients and manage pending invitations."}
          </DialogDescription>
        </DialogHeader>
        <MentorSharingDialogContent workflowId={workflowId} />
      </DialogContent>
    </Dialog>
  );
}

export default MentorSharingDialog;
